import ResourceBundle from "sap/base/i18n/ResourceBundle";
import Localization from "sap/base/i18n/Localization";

/**
 * @namespace com.ang.portal.service
 */

/**
 * Resolves translatable strings declared in a plugin's `plugin.json` against
 * that plugin's own i18n bundle.
 *
 * Manifest strings are rendered by the shell (side navigation, dashboard KPI
 * tiles, the plugin admin page, the user plugin-variable form), i.e. outside
 * the plugin's UIComponent, so they cannot use the plugin's `i18n>` model. A
 * manifest may therefore write a reference instead of a literal:
 *
 *     "displayName": "{i18n>pluginName}"
 *     "kpis": [{ "label": "{i18n>kpiOpenInvoices}" }]
 *
 * References are looked up in `/plugins/<id>/frontend/i18n/i18n.properties` —
 * the same bundle the plugin's views already use — for the current session
 * locale. Plain strings pass through untouched, so manifests that predate this
 * convention render exactly as before.
 */

/** Matches a whole-string `{i18n>someKey}` reference. */
const REFERENCE_PATTERN = /^\s*\{i18n>([^}\s]+)\}\s*$/;

/** Bundle load per plugin, cached by plugin id. `null` = plugin ships no bundle. */
const bundleCache = new Map<string, Promise<ResourceBundle | null>>();

/** Resolved plugin manifests, cached by plugin id. */
const manifestCache = new Map<string, Promise<PluginI18nSettings>>();

interface PluginI18nSettings {
	supportedLocales?: string[];
	fallbackLocale: string;
}

/**
 * A synchronous resolver bound to one plugin's bundle. Returns the translated
 * text for a reference, the value itself for a literal, and `fallback` (or an
 * empty string) when the value is missing.
 */
export type PluginTextResolver = (
	value?: string,
	fallback?: string,
) => string;

const isReference = (value?: string): boolean =>
	typeof value === "string" && REFERENCE_PATTERN.test(value);

/**
 * Read `supportedLocales` / `fallbackLocale` from the plugin's own
 * manifest.json so the shell resolves texts through the exact fallback chain
 * the plugin declares for its views. Without this the bundle loader would
 * probe locales the plugin does not ship and log 404s.
 */
const loadI18nSettings = (pluginId: string): Promise<PluginI18nSettings> => {
	let cached = manifestCache.get(pluginId);
	if (cached !== undefined) return cached;

	cached = (async (): Promise<PluginI18nSettings> => {
		try {
			const response = await fetch(
				`/plugins/${encodeURIComponent(pluginId)}/frontend/manifest.json`,
			);
			if (!response.ok) return { fallbackLocale: "en" };
			const manifest = (await response.json()) as {
				"sap.ui5"?: {
					models?: {
						i18n?: {
							settings?: {
								supportedLocales?: string[];
								fallbackLocale?: string;
							};
						};
					};
				};
			};
			const settings = manifest["sap.ui5"]?.models?.i18n?.settings ?? {};
			return {
				supportedLocales: Array.isArray(settings.supportedLocales)
					? settings.supportedLocales
					: undefined,
				fallbackLocale:
					typeof settings.fallbackLocale === "string"
						? settings.fallbackLocale
						: "en",
			};
		} catch {
			return { fallbackLocale: "en" };
		}
	})();

	manifestCache.set(pluginId, cached);
	return cached;
};

const loadBundle = (pluginId: string): Promise<ResourceBundle | null> => {
	let cached = bundleCache.get(pluginId);
	if (cached !== undefined) return cached;

	cached = (async (): Promise<ResourceBundle | null> => {
		try {
			const settings = await loadI18nSettings(pluginId);
			return await ResourceBundle.create({
				url: `/plugins/${encodeURIComponent(pluginId)}/frontend/i18n/i18n.properties`,
				locale: Localization.getLanguage(),
				supportedLocales: settings.supportedLocales,
				fallbackLocale: settings.fallbackLocale,
				async: true,
			});
		} catch {
			// A plugin without an i18n bundle is valid — its manifest just has
			// no references to resolve.
			return null;
		}
	})();

	bundleCache.set(pluginId, cached);
	return cached;
};

const PluginI18nService = {
	/**
	 * Build a resolver for one plugin.
	 *
	 * `values` are the manifest strings about to be rendered. The plugin's
	 * bundle is fetched only when at least one of them is an `{i18n>key}`
	 * reference, so plugins with literal-only manifests cost no extra request.
	 */
	async getResolver(
		pluginId: string,
		values: Array<string | undefined>,
	): Promise<PluginTextResolver> {
		const bundle = values.some(isReference) ? await loadBundle(pluginId) : null;

		return (value?: string, fallback?: string): string => {
			if (typeof value !== "string" || value.length === 0) {
				return fallback ?? "";
			}
			const match = REFERENCE_PATTERN.exec(value);
			if (!match) return value;

			const key = match[1];
			// bIgnoreKeyFallback: a missing key yields undefined instead of the
			// raw key, so an untranslated manifest shows the caller's fallback
			// (usually the plugin's technical name) rather than "{i18n>...}".
			const text = bundle?.getText(key, undefined, true);
			return text ?? fallback ?? key;
		};
	},

	/** True when the value is an `{i18n>key}` reference rather than a literal. */
	isReference,

	/**
	 * Drop cached bundles. Call after a plugin is installed, updated or
	 * removed so the next render re-reads its texts.
	 */
	clear(pluginId?: string): void {
		if (pluginId) {
			bundleCache.delete(pluginId);
			manifestCache.delete(pluginId);
			return;
		}
		bundleCache.clear();
		manifestCache.clear();
	},
};

export default PluginI18nService;
